/**
 * Ice Storm Fusion Anomaly layer (operator-cop-doctrine §3B.8, §4.5; IMS §7.2).
 *
 * A SCORED queue anomaly: a pulsing dot in the Response-Level palette (same family
 * as the fused-hotspot dots — styles/responseLevel.ts) carrying an ICE glyph that
 * distinguishes it from a fire dot. Rendered under the Anomaly Queue (urgent-anomalies)
 * layer alongside the fused hotspots, CAP polygons, and smoke detections. This is the
 * act-register signal on top of the ambient Power Outages layer (§4.28, untouched).
 *
 * Imperative installer (MapView owns the viewer): requestRefresh / setVisible /
 * pick → IceStormAnomalyFeature → the ice-storm detail panel (Acknowledge / Dismiss).
 */
import * as Cesium from 'cesium';
import {
  fetchIceStormAnomalies,
  type BboxQuery,
  type IceStormAnomalyFeature,
  type IceStormAnomalyFeatureCollection,
} from '../api';
import { hotspotLevelStyle, isResponseLevel, RESPONSE_LEVEL_COLORS } from '../styles/responseLevel';
import {
  anomalyLevelBillboardColor,
  anomalyGate,
  anomalyScale,
  isAcknowledged,
  lifecycleStyle,
  liftAckedCountryDrop,
  scaleAnomalyStyle,
  bboxKey,
  ClearedGhostTracker,
  ACK_STEEL,
} from './anomalyMarker';
import { renderDiscGlyph } from '../discGlyphComposite';
import { ownershipRing } from '../styles/ownershipCue';
import { escalationSafeAnomalyResponseLevel } from '../anomalyResponseLevel';
import { isWithinCanadaScope } from '../canadaScope';
import { urgentRedRegistry, type UrgentRedMarker } from '../greenMotion';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';

const REFRESH_DEBOUNCE_MS = 300;
const ICE_PREFIX = 'iceanom:';
/** Registry key for the #255 red-yield reporting (matches the ds name). */
const YIELD_LAYER_KEY = 'ice-storm-anomalies';

export interface IceStormAnomaliesLayerOptions {
  onCount?: (n: number) => void;
  getVisible?: () => boolean;
  /** Marker geometry scale (#132 WALL profile). Default 1 (operator COP). */
  getScale?: () => number;
  /** WALL profile (#272, PART XI Ruling 1.6): lift the Law-3 acked
   *  country-zoom drop so acked-live anomalies pin ACK-STEEL at every wall
   *  zoom. Default false (operator COP — untouched). */
  getLiftAckedGate?: () => boolean;
  fetcher?: (bbox: BboxQuery, signal: AbortSignal) => Promise<IceStormAnomalyFeatureCollection>;
}

export interface IceStormAnomaliesLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  pick(entity: unknown): IceStormAnomalyFeature | null;
  destroy(): void;
}

export function installIceStormAnomaliesLayer(
  viewer: Cesium.Viewer,
  opts: IceStormAnomaliesLayerOptions = {},
): IceStormAnomaliesLayerHandle {
  const fetcher = opts.fetcher ?? fetchIceStormAnomalies;
  const ds = new Cesium.CustomDataSource('ice-storm-anomalies');
  void viewer.dataSources.add(ds);

  const entityToFeature = new Map<Cesium.Entity, IceStormAnomalyFeature>();
  const ghosts = new ClearedGhostTracker(viewer, 'ice-storm-anomalies');
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  let loadGen = 0;

  const render = (fc: IceStormAnomalyFeatureCollection, viewKey: string): void => {
    ds.entities.removeAll();
    entityToFeature.clear();
    const live: Array<{ id: string; position: Cesium.Cartesian3 }> = [];
    // #255 red-yield: collect this render's UNACKED urgents (with each marker's
    // own zoom gate) so green camera motion yields while any is in view.
    const urgent: UrgentRedMarker[] = [];
    for (const feature of fc.features) {
      const [lon, lat] = feature.geometry.coordinates;
      // Canada-scope render guard (finding 1, 2026-07-06 §5.1): skip ice-storm anomalies outside
      // Canada (shared-DB ocean test fixtures + continent-zoom fetch bbox). See canadaScope.ts.
      if (!isWithinCanadaScope(lon, lat)) continue;
      const position = Cesium.Cartesian3.fromDegrees(lon, lat);
      // #445-ws2 — the ESCALATION-SAFE disc level: the owning IC's operator pin layered OVER the
      // derived response_level (an IC escalation reddens/pulses + enlarges the disc; a de-escalation
      // can never bury a worse derived — a life-safety surface, matching ActiveFiresLayer).
      // operator_response_level is null for a non-owned anomaly ⇒ the pure derived (disc unchanged).
      const level = escalationSafeAnomalyResponseLevel(feature.properties);
      // Colour Law 3: acknowledged (workstate or own-status) → ACK-STEEL static,
      // drops from country zoom; otherwise the urgent level style (L3/L4 pulse).
      // PART XI Ruling 1.6 (wall only): the acked country-drop lifts so ACK-STEEL
      // pins render at every wall zoom.
      const base = scaleAnomalyStyle(
        liftAckedCountryDrop(
          lifecycleStyle(hotspotLevelStyle(level), feature.properties),
          feature.properties,
          opts.getLiftAckedGate?.() ?? false,
        ),
        opts.getScale?.() ?? 1,
      );
      // R4 (icon-urgency council, PER-FAMILY): L4 is the single pulsing class — drop
      // L3's pulse so only the unacked L4 fill-breathes on the disc.
      const style =
        base.pulse && base.level !== 4 ? { ...base, pulse: false, pulseAmplitude: 0 } : base;
      const gate = anomalyGate(style);
      // Icon one-swing (#564, §C.1): the EXACT-ACT DISC — ONE billboard: a
      // RESPONSE-LEVEL-filled disc (ACK-STEEL when worked) with the white `ice-storm`
      // glyph knocked out. RETIRES the point-dot + white-plate CHIP + L4 GLOW-halo. L4
      // pulse = fill-breathe on billboard.color; L3 static; #410 country declutter via scaleByDistance.
      const worked = style.color === ACK_STEEL;
      const fillHex = worked
        ? ACK_STEEL
        : isResponseLevel(level)
          ? RESPONSE_LEVEL_COLORS[level]
          : RESPONSE_LEVEL_COLORS[1];
      // Ownership cue (feat/ownership-coordination) — 3-way viewer-relative keyline baked into the
      // disc; ADDITIVE, never touches fill/size/opacity.
      const ring = ownershipRing(feature.properties.owned_incident) ?? undefined;
      const image = renderDiscGlyph('ice-storm', { fill: fillHex, ring }) ?? undefined;
      const dot = ds.entities.add({
        id: `${ICE_PREFIX}${feature.id}`,
        position,
        billboard: {
          image,
          width: style.baseSize,
          height: style.baseSize,
          color: anomalyLevelBillboardColor(!worked && style.pulse, style.alpha),
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          distanceDisplayCondition: gate,
          scaleByDistance: anomalyScale(style), // #410: declutter the always-on L3/L4 at country zoom
        },
      });
      entityToFeature.set(dot, feature);
      live.push({ id: feature.id, position });
      if (!isAcknowledged(feature.properties)) {
        urgent.push({
          lon,
          lat,
          visibleFarM: style.hideAtCountry ? REGIONAL_VISIBLE_FAR_M : Number.POSITIVE_INFINITY,
        });
      }
    }
    urgentRedRegistry.report(YIELD_LAYER_KEY, urgent);
    // Law 3 cleared-ghost: markers that departed on this same view fade to a grey
    // ghost for ~60s, then auto-leave.
    ghosts.reconcile(live, viewKey);
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      render(fc, bboxKey(bbox));
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
      urgentRedRegistry.setVisible(YIELD_LAYER_KEY, ds.show);
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchIceStormAnomalies failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
      ghosts.setVisible(visible);
      // #255 — a hidden layer's urgents are not in view: release the red-yield.
      urgentRedRegistry.setVisible(YIELD_LAYER_KEY, visible);
    },
    pick(entity: unknown): IceStormAnomalyFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      ghosts.destroy();
      urgentRedRegistry.clear(YIELD_LAYER_KEY);
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
